import { createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';
import moment from 'moment';

import { APIClient } from '../../helpers/api_helper';
import { ChinaGeoCoordMap, coordInRegion } from '../../Libs/echarts';
import * as ToastHelper from '../../helpers/toast_helper';

/**
 * 统计信息的单例模式, 用于存储通过日志等数据联合得到的统计数据
 */
const localState = {
    /**
     *  Uninitialized | Prepareing | Ready
     */
    dashboardDataStatus: 'Uninitialized',
    dashboardData: {}
};

/**
 * 将日志解析为{警告类型:警告次数}这样的数据结构,用于实现警告(报警)的归类统计
 */
function parseAlarmLog4TypeCount(log) {
    log = log || '';

    const logList = [];
    if (!log.includes('警报')) {
        return logList;
    }

    const lines = log.split('\n');
    for (let line of lines) {
        line = _.trim(line);
        const matches = line.match(/警报[：:]\s*(\S+)\s+\S*数[：:]\D*(\d+)\D?/);
        if (_.isNil(matches) || _.isEmpty(matches)) {
            continue;
        }

        const type = _.trim(matches[1]);
        const count = parseInt(matches[2]);
        logList.push({ type, count });
    }

    return logList;
}

const prefix = 'statistic';
export const alarm = createAsyncThunk(`${prefix}/alarm`, async function (payload) {
    const { data } = await APIClient.get(`/statistics/alarm?startDate=${payload.startDate}&endDate=${payload.endDate}`);
    return data;
});

export const user = createAsyncThunk(`${prefix}/user`, async function () {
    const { data } = await APIClient.get('/statistics/user');
    return data;       
});

export const project = createAsyncThunk(`${prefix}/project`, async function () {
    const { data } = await APIClient.get('/statistics/project');
    return data;
});

export const alarmType = createAsyncThunk(`${prefix}/alarmType`, async function (payload) {    
    const { role  } = payload;
    if (role === 'ProjectAdmin') {
        const { data } = await APIClient.get(`/statistics/vehicle-alarm-type/site?startDate=${payload.startDate}&endDate=${payload.endDate}`);
        return data;
    }
    if (role === 'ProjectSupervisor') {
        const { data } = await APIClient.get(`/statistics/vehicle-alarm-type/vehicle?startDate=${payload.startDate}&endDate=${payload.endDate}`);
        return data;
    }
    const { data } = await APIClient.get(`/statistics/vehicle-alarm-type/project?startDate=${payload.startDate}&endDate=${payload.endDate}`);
    return data;
});

export const alarmsCount = createAsyncThunk(`${prefix}/alarmsCount`, async function (payload) {    
    const { role  } = payload;
    if (role === 'ProjectSupervisor') {
        const { data } = await APIClient.get(`/statistics/vehicle-alarm-count/vehicle?startDate=${payload.startDate}&endDate=${payload.endDate}`);
        return data;
    }
    if (role === 'ProjectAdmin') {
        const { data } = await APIClient.get(`/statistics/vehicle-alarm-count/site?startDate=${payload.startDate}&endDate=${payload.endDate}`);
        return data;
    }
    const { data } = await APIClient.get(`/statistics/vehicle-alarm-count/project?startDate=${payload.startDate}&endDate=${payload.endDate}`);
    return data;
});

export const resetAlarm = createAsyncThunk(`${prefix}/resetAlarm`, async function (id) { 
    try {
        await APIClient.put(`/statistics/vehicle-alarm-count/alarm-count-reset/${id}`);
        ToastHelper.resetSucceeded('报警次数');
    } catch (error) {
        ToastHelper.resetFailed('报警次数');
        throw error;
    }  
});

export const onlineTime = createAsyncThunk(`${prefix}/onlineTime`, async function (payload) {    
    const { role } = payload;
    if (role === 'ProjectSupervisor') {
        const { data } = await APIClient.get(`/statistics/vehicle-online-duration/vehicle?startDate=${payload.startDate}&endDate=${payload.endDate}`);
        return data;
    }
    if (role === 'ProjectAdmin') {
        const { data } = await APIClient.get(`/statistics/vehicle-online-duration/site?startDate=${payload.startDate}&endDate=${payload.endDate}`);
        return data;
    }
    const { data } = await APIClient.get(`/statistics/vehicle-online-duration/project?startDate=${payload.startDate}&endDate=${payload.endDate}`);
    return data;
});

export const reset = createAsyncThunk(`${prefix}/reset`, async function (id) { 
    try {
        await APIClient.put(`/statistics/vehicle-online-duration/onlineSession/${id}`);
        ToastHelper.resetSucceeded('离线时长');
    } catch (error) {
        ToastHelper.resetFailed('离线时长');
        throw error;
    }  
});

export const faultyStarts = createAsyncThunk(`${prefix}/faultyStarts`, async function (payload) {    
    const { data } = await APIClient.get(`/statistics/vehicle-abnormal-start?startDate=${payload.startDate}&endDate=${payload.endDate}`);
    return data;
});

export const dashboard = createAsyncThunk(`${prefix}/dashboard`, async function () {
    if (localState.dashboardDataStatus !== 'Uninitialized') {
        return localState.dashboardData;
    }
    localState.dashboardDataStatus = 'Prepareing';
    let panic = null;

    try {
        const vResponse = await APIClient.get('/vehicles');
        // const sResponse = await APIClient.get('/sites');
        const vehicles = vResponse.data.filter(vehicle => vehicle.isAlive);
        // const sites = sResponse.data;
        const vehicleCountByProvince = {};
        for (const vehicle of vehicles) {
            // const site = sites.find(x => x.id === vehicle.siteId);
            // vehicle.siteName = site?.siteName;
            // vehicle.siteOwnerName = site?.ownerName;
            // vehicle.projectId = site?.projectId;
            // vehicle.projectName = site?.projectName;

            const vehicleId = vehicle?.id;
            if (!vehicleId) {
                continue;
            }

            const hPromise = APIClient.get(`/vehicles/${vehicleId}/last-heartbeat`)
                .then(rsp => rsp.data)
                .catch(err => console.log('Get last heartbeat failed:', err));
            const heartbeat = await hPromise;
            const rmc = (heartbeat || {}).rmc;
            if (rmc) {
                const region = coordInRegion(rmc.longitude, rmc.latitude);
                if (region) {
                    const name = region.name;
                    vehicleCountByProvince[name] = (vehicleCountByProvince[name] || 0) + 1;
                }
            }
        }

        const chartData = [];
        for (const [key, value] of _.entries(vehicleCountByProvince)) {
            const item = ChinaGeoCoordMap[key];
            if (!item) {
                continue;
            }

            chartData.push({ name: key, value: [...item.cp, value] });
        }
        localState.dashboardData = { vehicleDistributionMapData: chartData, vehicles: vehicles };
        return _.cloneDeep(localState.dashboardData);
    } catch (error) {
        console.log('Prepareing dashboard failed:', error);
        panic = error;
    }

    localState.dashboardDataStatus = panic ? 'Uninitialized' : 'Ready';
    if (panic) {
        throw panic;
    }

    return localState.dashboardData;
});

export const alarmsTypeCountProject = createAsyncThunk(`${prefix}/alarmsTypeCountProject`, async function (payload) {    
    const { data } = await APIClient.get(`/statistics/vehicle-alarm-count/type-project?startDate=${payload.startDate}&endDate=${payload.endDate}`);
    return data;
});

export const alarmsTypeCountSite = createAsyncThunk(`${prefix}/alarmsTypeCountSite`, async function (payload) {    
    const { data } = await APIClient.get(`/statistics/vehicle-alarm-count/type-site?startDate=${payload.startDate}&endDate=${payload.endDate}`);
    return data;
});

export const alarmsTypeCountVehicle = createAsyncThunk(`${prefix}/alarmsTypeCountVehicle`, async function (payload) {    
    let url = `/statistics/vehicle-alarm-count/type-vehicle?startDate=${payload.startDate}&endDate=${payload.endDate}`;
    
    // 如果有 projectId，添加到 URL
    if (payload.projectId) {
        url += `&projectId=${payload.projectId}`;
    }
    
    // 如果有 siteId，添加到 URL
    if (payload.siteId) {
        url += `&siteId=${payload.siteId}`;
    }
    
    const { data } = await APIClient.get(url);
    return data;
});

export const alarmsDailyCountProject = createAsyncThunk(`${prefix}/alarmsDailyCountProject`, async function () {    
    const { data } = await APIClient.get(`/statistics/vehicle-alarm-count/daily-project`);
    return data;
});